import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { UserProfileComponent } from '../../common/user-profile/user-profile.component';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { MbLanguageSwitcherComponent, MbLogoComponent } from '../../common';
import { MenuItem } from '../../../core/services/menu.service';
import { ASSET_CONSTANTS } from '../../../shared/constants/asset-constants';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { MbNotificationsComponent } from '../../common/mb-notifications/mb-notifications.component';
import { NotificationsService } from '../../../core/services/notifications.service';
import { UserRole } from '../../../utils/enums/user-roles.enum';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { HasPermissionDirective } from '../../common/mb-permission-directive/mb-permission-directive.component';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NzBreadCrumbModule,
    NzMenuModule,
    NzLayoutModule,
    NzButtonModule,
    NzBadgeModule,
    UserProfileComponent,
    MbLogoComponent,
    NzIconDirective,
    NzDrawerModule,
    MbNotificationsComponent,
    NzSelectModule,
    TranslocoDirective,
    FormsModule,
    HasPermissionDirective,
    MbLanguageSwitcherComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.css'],
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  isCollapsed = false;
  isMobile = false;
  isMedium = false;
  isMobileMenuOpen = false;
  isNotificationsDrawerVisible = false;
  private notificationsService = inject(NotificationsService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  unreadNotificationsCount = 0;
  protected currentUserRole = localStorage.getItem('userType') || 'CITIZEN';
  private transloco = inject(TranslocoService);

  currentLang = this.transloco.getActiveLang();

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 640;
    this.isMedium = window.innerWidth >= 640 && window.innerWidth < 1024;
    if (!this.isMobile && wasMobile) {
      this.isMobileMenuOpen = false;
    }
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth < 640;
    this.isMedium = window.innerWidth >= 640 && window.innerWidth < 1024;
    this.loadNotifications();
    this.currentLang = this.transloco.getActiveLang() || 'en';
  }

  ngOnDestroy(): void {
    // The layout only lives while the user is signed in (AuthGuard), so tearing it
    // down — e.g. on logout — is the right moment to stop the badge poll.
    this.notificationsService.stopPolling();
  }

  onLanguageChange(lang: string): void {
    this.transloco.setActiveLang(lang);
    this.currentLang = lang;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  /**
   * Mentions badge: polls the cheap unread-count endpoint (60 s, paused while the tab
   * is hidden). The heavy organisation-broadcast feed is intentionally not consumed.
   */
  private loadNotifications(): void {
    this.notificationsService.unreadCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => {
        this.unreadNotificationsCount = count;
      });
    this.notificationsService.startPolling();
  }

  openNotificationsDrawer(): void {
    this.isNotificationsDrawerVisible = true;
  }

  closeNotificationsDrawer = (): void => {
    this.isNotificationsDrawerVisible = false;
    // The user may have read items in the drawer; resync the badge with the server.
    this.notificationsService.refreshUnreadCount();
  };

  isComplaintsActive(): boolean {
    const url = this.router.url;
    return (
      url.includes('/dashboard/complaints') ||
      url.includes('/dashboard/cases') ||
      url.includes('/dashboard/analytics')
    );
  }

  menuItems: MenuItem[] = [
    {
      key: 'dashboard',
      label: 'dashboard',
      route: '/dashboard',
      children: [
        {
          key: 'complaints',
          label: 'complaints',
          icon: ASSET_CONSTANTS.I_bubble_chat,
          route: this.getCasePath(),
          permissions: ['VIEW_COMPLAINT', 'VIEW_CASE'],
          numbers: 12,
          showBadge: true,
          children: [
            {
              key: 'cases',
              label: 'cases',
              route: '/dashboard/cases',
              permissions: 'VIEW_CASE',
            },
            {
              key: 'analytics',
              label: 'analytics',
              route: '/dashboard/analytics',
              permissions: 'VIEW_CASE_ANALYTICS',
            },
          ],
        },
        {
          key: 'announcements',
          label: 'announcements',
          icon: ASSET_CONSTANTS.I_bell,
          route: '/dashboard/announcements',
          permissions: 'MANAGE_ANNOUNCEMENT',
        },
        {
          key: 'tipoffs',
          label: 'tipOffs',
          icon: ASSET_CONSTANTS.I_tipoff,
          route: '/dashboard/tipoffs',
          permissions: 'VIEW_TIP_OFF',
        },
      ],
    },
    {
      key: 'manage',
      label: 'manage',
      icon: 'setting',
      children: [
        {
          key: 'citizens',
          label: 'citizens',
          icon: ASSET_CONSTANTS.I_user,
          route: '/dashboard/citizens',
          permissions: 'VIEW_CITIZEN',
        },
        {
          key: 'institutions',
          label: 'institutions',
          icon: ASSET_CONSTANTS.I_bank,
          route: '/dashboard/organization',
          permissions: 'VIEW_ORGANIZATION',
        },
        {
          key: 'accounts',
          label: 'accounts',
          icon: ASSET_CONSTANTS.I_customer_service,
          route: '/dashboard/accounts',
          permissions: 'VIEW_STAFF',
        },
        {
          key: 'security',
          label: 'security',
          icon: ASSET_CONSTANTS.I_gear,
          route: '/dashboard/security',
          permissions: 'VIEW_TIP_OFF_CATEGORY',
        },
        {
          key: 'e-citizen',
          label: 'E-citizen',
          icon: ASSET_CONSTANTS.I_user,
          route: '/dashboard/e-citizen',
          permissions: 'VIEW_CITIZEN',
        },
      ],
    },
  ];

  toggleCollapsed(): void {
    if (this.isMobile) {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
    } else {
      this.isCollapsed = !this.isCollapsed;
    }
  }

  hasChildren(item: MenuItem): boolean {
    return !!(item.children && item.children.length > 0);
  }

  hasVisibleChildren(item: MenuItem): boolean {
    if (!this.hasChildren(item)) {
      return false;
    }
    return item.children!.some((child) => child.permissions);
  }

  getCasePath(): string {
    return this.currentUserRole === UserRole.SUPER_ADMIN
      ? '/dashboard/complaints'
      : '/dashboard/cases';
  }

  protected readonly ASSET_CONSTANTS = ASSET_CONSTANTS;
}
